// pages/Oati/OattComputationPage.tsx

import { useState } from 'react';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@constants/pagination';
import { useOattSession } from '@hooks/useOattSession';
import { Table, Empty, Spin, message, Tag } from 'antd';
import type { SelectProps, TablePaginationConfig } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useQuery } from '@apollo/client';
import { GET_OATT_COMPUTED } from '@graphql/oattcomputation.gql';
import utc from 'dayjs/plugin/utc';
import PageLayout from '@components/layout/PageLayout';
import { NSPDatePicker } from '@components/ui/NSPDatepicker';
import styles from './OattPages.module.css';
import { NSPButton } from '@components/ui/NSPButton';
import { useNavigate } from 'react-router-dom';
import { useOattCustomer } from '@hooks/useOattcustomer';
import useSession from '@hooks/useSession';
import { testPaginationItemRender, toTestAttrValue, withTestColumnAttrs, type TestAttrs } from '@utils/testAttr';

dayjs.extend(utc);

interface OattComputedRecord {
  oatt_computed_id: number;
  raw_id: number;
  seller: string;
  aref: string;
  customer_code: string;
  billing_period: string;
  billing_type: string;
  mw_granted: number | null;
  billing_rate: number | null;
  hours: number | null;
  start_time: string | null;
  stop_time: string | null;
  duration_value: number | null;
  duration_unit: string | null;
  peak: string | null;
  dow: number | null;
  on_peak_hours: number | null;
  off_peak_hours: number | null;
  firm_transmission_rate: number | null;
  nonfirm_transmission_rate: number | null;
  ancillary_schedule1_rate: number | null;
  ancillary_schedule2_rate: number | null;
  firm_transmission_charge_computed: number | null;
  nonfirm_transmission_charge_computed: number | null;
  ancillary_schedule1_computed: number | null;
  ancillary_schedule2_computed: number | null;
  is_revision: boolean | null;
  revision_no: number | null;
}

const fmtMoney = (v: number | null | undefined) => {
  if (v == null) return '—';
  return Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtRate = (v: number | null | undefined) => {
  if (v == null || v === 0) return '—';
  return Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDateTime = (v: string | null | undefined): string => {
  if (!v) return '—';
  if (/^\d{10,13}$/.test(v.trim())) {
    const ms = v.trim().length === 13 ? Number(v) : Number(v) * 1000;
    const d = new Date(ms);
    if (isNaN(d.getTime())) return '—';
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()} ${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  const cleaned = v.replace('T', ' ').replace(/\.\d+$/, '').trim();
  const [datePart, timePart = '00:00'] = cleaned.split(' ');
  const [yyyy, mm, dd] = datePart.split('-');
  const [hh, mi] = timePart.split(':');
  if (!yyyy || !mm || !dd) return '—';
  return `${parseInt(mm)}/${parseInt(dd)}/${yyyy} ${parseInt(hh)}:${mi}`;
};

const fmtDateOnly = (v: string | null | undefined): string => {
  if (!v) return '—';
  const datePart = v.split('T')[0].split(' ')[0];
  const [yyyy, mm, dd] = datePart.split('-');
  if (!yyyy || !mm || !dd) return '—';
  return `${parseInt(mm)}/${parseInt(dd)}/${yyyy}`;
};

const columns: ColumnsType<OattComputedRecord> = [
  { title: 'Seller', dataIndex: 'seller', key: 'seller', width: 80, fixed: 'left', render: (v) => v || '—' },
  { title: 'AREF', dataIndex: 'aref', key: 'aref', width: 110, fixed: 'left', render: (v) => v ?? '—' },
  { title: 'Customer', dataIndex: 'customer_code', key: 'customer_code', width: 90, fixed: 'left', render: (v) => v || '—' },
  { title: 'Billing Period', dataIndex: 'billing_period', key: 'billing_period', width: 110, fixed: 'left', render: fmtDateOnly },
  { title: 'Billing Type', dataIndex: 'billing_type', key: 'billing_type', width: 110, fixed: 'left', render: (v) => v || '—' },
  { title: 'MW Granted', dataIndex: 'mw_granted', key: 'mw_granted', width: 100, align: 'right', render: (v: number | null) => v != null ? Number(v).toLocaleString() : '—' },
  { title: 'Billing Rate', dataIndex: 'billing_rate', key: 'billing_rate', width: 110, align: 'right', render: (v: number | null) => v != null ? `$${Number(v).toFixed(4)}` : '—' },
  { title: 'Hours', dataIndex: 'hours', key: 'hours', width: 80, align: 'right', render: (v: number | null) => v != null ? Number(v).toLocaleString() : '—' },
  { title: 'Start Time', dataIndex: 'start_time', key: 'start_time', width: 140, render: fmtDateTime },
  { title: 'Stop Time', dataIndex: 'stop_time', key: 'stop_time', width: 140, render: fmtDateTime },
  {
    title: 'Duration', key: 'duration', width: 110, align: 'center',
    render: (_, r) => r.duration_value == null ? '—' : `${r.duration_value} ${r.duration_unit ?? ''}`.trim(),
  },
  {
    title: 'Peak', dataIndex: 'peak', key: 'peak', width: 70, align: 'center',
    render: (v) => !v ? '—' : <Tag color={v === 'ON' ? 'green' : 'red'} style={{ fontSize: 11, fontWeight: 700 }} data-testid="peak-tag" data-peak={toTestAttrValue(v)}>{v}</Tag>,
  },
  { title: 'DOW', dataIndex: 'dow', key: 'dow', width: 60, align: 'center', render: (v) => v },
  { title: 'On Peak Hours', dataIndex: 'on_peak_hours', key: 'on_peak_hours', width: 120, align: 'right', render: (v) => v ?? '—' },
  { title: 'Off Peak Hours', dataIndex: 'off_peak_hours', key: 'off_peak_hours', width: 120, align: 'right', render: (v) => v ?? '—' },
  {
    title: 'Transmission Service Rate',
    children: [
      { title: 'Schedule 7', dataIndex: 'firm_transmission_rate', key: 'firm_rate', width: 110, align: 'right', render: fmtRate },
      { title: 'Schedule 8', dataIndex: 'nonfirm_transmission_rate', key: 'nonfirm_rate', width: 120, align: 'right', render: fmtRate },
    ],
  },
  {
    title: 'Ancillary Services Rate',
    children: [
      { title: 'Schedule 1', dataIndex: 'ancillary_schedule1_rate', key: 'sc1_rate', width: 110, align: 'right', render: fmtRate },
      { title: 'Schedule 2', dataIndex: 'ancillary_schedule2_rate', key: 'sc2_rate', width: 110, align: 'right', render: fmtRate },
    ],
  },
  {
    title: 'Transmission Service Charge',
    children: [
      { title: 'Schedule 7', dataIndex: 'firm_transmission_charge_computed', key: 'firm_charge', width: 130, align: 'right', render: fmtMoney },
      { title: 'Schedule 8', dataIndex: 'nonfirm_transmission_charge_computed', key: 'nonfirm_charge', width: 130, align: 'right', render: fmtMoney },
    ],
  },
  {
    title: 'Ancillary Services Charge',
    children: [
      { title: 'Schedule 1', dataIndex: 'ancillary_schedule1_computed', key: 'sc1_charge', width: 130, align: 'right', render: fmtMoney },
      { title: 'Schedule 2', dataIndex: 'ancillary_schedule2_computed', key: 'sc2_charge', width: 130, align: 'right', render: fmtMoney },
    ],
  },
];

// Schedule columns repeat titles across groups, so cells are named by group + schedule
const GROUPED_COLUMN_LABELS: Record<string, string> = {
  firm_rate: 'Transmission Rate Schedule 7',
  nonfirm_rate: 'Transmission Rate Schedule 8',
  sc1_rate: 'Ancillary Rate Schedule 1',
  sc2_rate: 'Ancillary Rate Schedule 2',
  firm_charge: 'Transmission Charge Schedule 7',
  nonfirm_charge: 'Transmission Charge Schedule 8',
  sc1_charge: 'Ancillary Charge Schedule 1',
  sc2_charge: 'Ancillary Charge Schedule 2',
};

const testColumns = withTestColumnAttrs(
  columns.map((col) =>
    'children' in col
      ? { ...col, children: withTestColumnAttrs(col.children, 'oatt-computed-value', GROUPED_COLUMN_LABELS) }
      : col,
  ),
  'oatt-computed-value',
);

const OattComputationPage = () => {
  const { selectedDate, setSelectedDate } = useOattSession();
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const { customerId, customerCode, CustomerSelect } = useOattCustomer();
  const { isOattBillApprover } = useSession();

  const selectedMonth = selectedDate.month() + 1;
  const selectedYear = selectedDate.year();

  const { data, loading, error } = useQuery<{ getOattComputed: OattComputedRecord[] }>(
    GET_OATT_COMPUTED,
    {
      variables: { month: selectedMonth, year: selectedYear, customerId },
      skip: customerId === null,
      fetchPolicy: 'no-cache',
    },
  );

  const records = data?.getOattComputed ?? [];

  const isRevisionData = records.length > 0 && Boolean(records[0].is_revision);
  const pageTitle = `${customerCode ? `${customerCode} - ` : ''
    }${isRevisionData ? 'Revised ' : ''
    }${selectedDate.format('MMMM YYYY')} OATT QC Computation`;

  const handleValidate = () => {
    if (records.length === 0) {
      messageApi.warning('No computed records found. Please run QC Compute OATT first.');
      return;
    }
    navigate('/oati/validation');
  };

  const disabledDate = (current: Dayjs) => current && current >= dayjs().startOf('month');

  return (
    <>
      {contextHolder}
      <PageLayout
        title={pageTitle}
        headerAccessoryView={
          <div className={styles.periodSelector} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className={styles.customerSelectWrapper}>
              <CustomerSelect />
            </div>
            <NSPDatePicker
              picker="month"
              format="MMM YYYY"
              allowClear={false}
              value={selectedDate}
              onChange={(date) => date && setSelectedDate(date)}
              disabledDate={disabledDate}
              testId="month-picker"
            />
          </div>
        }
      >
        <div className={styles.content}>
          {error ? (
            <Empty description={`Error loading data: ${error.message}`} data-testid="oatt-computed-error" />
          ) : (
            <Table<OattComputedRecord>
              className={styles.oattTable}
              dataSource={records.map((r) => ({ ...r, key: r.oatt_computed_id ?? r.raw_id }))}
              columns={testColumns}
              loading={loading ? { indicator: <Spin size="large" /> } : false}
              rowKey={(r) => r.oatt_computed_id ?? r.raw_id}
              data-testid="oatt-computed-table"
              data-loading={String(loading)}
              data-revision={String(isRevisionData)}
              onRow={(record) =>
                ({
                  'data-testid': 'oatt-computed-row',
                  'data-id': String(record.oatt_computed_id ?? record.raw_id),
                  'data-aref': record.aref ?? '',
                }) as TestAttrs
              }
              pagination={{
                pageSize,
                showTotal: (total) => <span data-testid="pagination-total">{`Total ${total} records`}</span>,
                showSizeChanger: { 'data-testid': 'page-size-select' } as SelectProps,
                pageSizeOptions: PAGE_SIZE_OPTIONS,
                onShowSizeChange: (_, size) => setPageSize(size),
                itemRender: testPaginationItemRender,
                'data-testid': 'table-pagination',
              } as TablePaginationConfig}
              scroll={{ x: 'max-content', y: 'calc(100vh - 320px)' }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    data-testid="oatt-computed-empty"
                    description={
                      customerId === null
                        ? 'Select a customer above'
                        : `No computed data for ${selectedDate.format('MMMM YYYY')}. Run QC Compute OATT on the Data page first.`
                    }
                  />
                ),
              }}
            />
          )}
        </div>

        <div className={styles.computeButtonContainer}>
          <NSPButton onClick={handleValidate} disabled={records.length === 0 || customerId === null || isOattBillApprover} data-testid="validate-oatt-button">
            VALIDATE OATT
          </NSPButton>
        </div>
      </PageLayout>
    </>
  );
};

export default OattComputationPage;